import { useState } from 'react';
import {
  COMMUNITY_AMENITIES_NOTE,
  DISTRICTS,
  NEIGHBORHOODS,
  NEIGHBORHOODS_AS_OF,
  type District,
  type Neighborhood,
} from '@/lib/neighborhoods';
import { ChevronRightIcon } from '@/components/Icons';

/**
 * Detail sections in the expanded panel, each label with its own color
 * from the app palette (deepened where the raw token is too light on white).
 */
const FIELDS: { key: 'priceRange' | 'built' | 'amenities' | 'forSale'; label: string; color: string }[] = [
  { key: 'priceRange', label: 'Price range', color: '#173D4E' },
  { key: 'built', label: 'Age', color: '#2E7CA8' },
  { key: 'amenities', label: 'Amenities', color: '#2F7D57' },
  { key: 'forSale', label: 'Homes for sale', color: '#9A6B0F' },
];

/** Town-wide listing scale the price bars are drawn against, in $K. */
const SCALE_MIN_K = 200;
const SCALE_MAX_K = 1600;

function PriceBar({ n }: { n: Neighborhood }) {
  if (n.priceMinK == null) {
    return (
      <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: 'var(--color-ink)' }}>{n.priceText}</div>
    );
  }
  const span = SCALE_MAX_K - SCALE_MIN_K;
  const left = Math.max(0, Math.min(100, ((n.priceMinK - SCALE_MIN_K) / span) * 100));
  const width =
    n.priceMaxK != null
      ? Math.max(4, ((n.priceMaxK - n.priceMinK) / span) * 100)
      : (100 - left) * 0.7;
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          position: 'relative',
          height: 6,
          borderRadius: 999,
          background: 'var(--color-line)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${left}%`,
            width: `${width}%`,
            borderRadius: 999,
            background:
              n.priceMaxK != null
                ? 'var(--color-primary)'
                : 'linear-gradient(90deg, var(--color-primary), rgba(23,61,78,0.15))',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 4,
          fontSize: 12,
          color: 'var(--color-ink-muted)',
        }}
      >
        <span style={{ fontWeight: 700, color: 'var(--color-ink)' }}>{n.priceText}</span>
        <span>
          town scale ${SCALE_MIN_K}K–$1.6M
        </span>
      </div>
    </div>
  );
}

function Chips({ n, district }: { n: Neighborhood; district: District }) {
  const chips: string[] = [];
  if (n.gated) chips.push('Gated');
  if (n.fiftyFivePlus) chips.push('55+');
  if (n.golf) chips.push('Golf');
  chips.push(n.status);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {chips.map((c) => (
        <span
          key={c}
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '3px 8px',
            borderRadius: 999,
            color: 'var(--color-ink)',
            background: `${district.color}26`,
            border: `1px solid ${district.color}59`,
          }}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

function ForSalePill({ n, district }: { n: Neighborhood; district: District }) {
  let text: string | null = null;
  if (n.forSaleCount != null) text = `${n.forSaleCount} for sale`;
  else if (n.status.startsWith('Pre-sales')) text = 'Pre-sales';
  else if (n.status === 'Coming soon') text = 'Coming soon';
  else if (n.status === 'Rental community') text = 'Rentals';
  else if (n.id === 'terrawalk') text = 'New homes';
  if (!text) return null;
  return (
    <span
      style={{
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 700,
        padding: '3px 9px',
        borderRadius: 999,
        background: district.color,
        color: '#fff',
      }}
    >
      {text}
    </span>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: 'var(--color-sand)', borderRadius: 10, padding: '10px 12px' }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--color-ink-muted)',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, lineHeight: 1.25 }}>{value}</div>
    </div>
  );
}


/** Deepened district color for small headings on light backgrounds. */
function districtTextColor(d: District): string {
  switch (d.id) {
    case 'westtown':
      return '#173D4E';
    case 'lake-babcock':
      return '#2E7CA8';
    case 'national':
      return '#2F7D57';
    case 'midtown':
      return '#9A6B0F';
  }
}

function NeighborhoodRow({
  n,
  district,
  last,
  open,
  onToggle,
}: {
  n: Neighborhood;
  district: District;
  last: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const forSaleTile =
    n.forSaleCount != null
      ? `~${n.forSaleCount} listed`
      : n.status.startsWith('Pre-sales')
        ? 'Pre-sales'
        : n.status === 'Coming soon'
          ? 'Not yet selling'
          : n.status === 'Rental community'
            ? 'Rentals only'
            : n.id === 'terrawalk'
              ? 'Builder homes'
              : 'Not tracked';
  return (
    <div style={last ? undefined : { borderBottom: '1px solid var(--color-line)' }}>
      <button
        type="button"
        className="list-row"
        style={{
          width: '100%',
          padding: '16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          alignItems: 'flex-start',
        }}
        aria-expanded={open}
        onClick={onToggle}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ flex: 1, minWidth: 0 }}>{n.name}</strong>
            <ForSalePill n={n} district={district} />
          </span>
          <Chips n={n} district={district} />
          <PriceBar n={n} />
          {!open && (
            <p style={{ margin: '10px 0 0', fontSize: 14, lineHeight: 1.45 }}>
              <strong style={{ color: districtTextColor(district) }}>The take: </strong>
              {n.takeShort}
            </p>
          )}
        </span>
        <ChevronRightIcon
          style={{
            flexShrink: 0,
            marginTop: 2,
            transform: open ? 'rotate(90deg)' : undefined,
            transition: 'transform 0.15s ease',
          }}
        />
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <a href={n.photoPage} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
            <img
              src={n.photoUrl}
              alt={n.name}
              loading="lazy"
              style={{
                width: '100%',
                height: 190,
                objectFit: 'cover',
                borderRadius: 12,
                display: 'block',
                background: district.color,
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </a>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginTop: 6,
              fontSize: 12,
              color: 'var(--color-ink-muted)',
            }}
          >
            <span>Photo from the neighborhood&rsquo;s sales page</span>
            <a href={n.photoPage} target="_blank" rel="noreferrer" style={{ fontWeight: 700 }}>
              View sales page &rarr;
            </a>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '12px 0 14px' }}>
            <StatTile label="Price range" value={n.priceText} />
            <StatTile label="For sale" value={forSaleTile} />
            <StatTile label="Age" value={n.ageShort} />
            <StatTile label="Builder" value={n.builder} />
          </div>

          <div
            style={{
              borderLeft: '3px solid ' + district.color,
              background: 'var(--color-sand)',
              borderRadius: '0 10px 10px 0',
              padding: '10px 12px',
              margin: '0 0 14px',
            }}
          >
            <span className="section-title" style={{ display: 'block', marginBottom: 4, color: districtTextColor(district) }}>
              The take
            </span>
            <p style={{ margin: 0 }}>{n.narrative}</p>
          </div>

          {FIELDS.map((f) => (
            <p key={f.key} style={{ margin: '0 0 10px' }}>
              <span className="section-title" style={{ display: 'block', marginBottom: 2, color: f.color }}>
                {f.label}
              </span>
              {n[f.key]}
            </p>
          ))}
          <p style={{ margin: 0, color: 'var(--color-ink-muted)', fontSize: 13 }}>Sources: {n.sourceNote}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Every Babcock Ranch neighborhood, grouped by where it sits in town
 * (the developer's WestTown / MidTown districts, the Lake Babcock shore,
 * and the Babcock National enclave). Rows are mini-infographics — status
 * chips, a price bar against the town-wide scale, a for-sale pill — and
 * tapping expands an entry to its sales-page photo, stat tiles and the
 * full write-up. Data is the dated snapshot in src/lib/neighborhoods.ts.
 */

type TypeFilter = 'all' | 'gated' | '55+' | 'golf' | 'rentals';
type PriceFilter = 'any' | 'u350' | 'm3560' | 'o600';
type StatusFilter = 'all' | 'building' | 'established' | 'notyet';

function matchesFilters(n: Neighborhood, type: TypeFilter, price: PriceFilter, status: StatusFilter): boolean {
  if (type === 'gated' && !n.gated) return false;
  if (type === '55+' && !n.fiftyFivePlus) return false;
  if (type === 'golf' && !n.golf) return false;
  if (type === 'rentals' && n.status !== 'Rental community') return false;
  if (price === 'u350' && !(n.priceMinK != null && n.priceMinK < 350)) return false;
  if (price === 'm3560' && !(n.priceMinK != null && n.priceMinK <= 600 && (n.priceMaxK ?? Number.POSITIVE_INFINITY) >= 350))
    return false;
  if (price === 'o600' && !((n.priceMaxK ?? n.priceMinK ?? 0) >= 600)) return false;
  if (status === 'building' && !(n.status.startsWith('Still building') || n.status.startsWith('New'))) return false;
  if (
    status === 'established' &&
    !['Built out', 'Resale only', 'Established', 'Established enclave'].includes(n.status)
  )
    return false;
  if (status === 'notyet' && !(n.status === 'Coming soon' || n.status.startsWith('Pre-sales'))) return false;
  return true;
}

function FilterRow<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 10 }}>
      <span
        style={{
          flexShrink: 0,
          width: 52,
          paddingTop: 5,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--color-ink-muted)',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              style={{
                fontSize: 12,
                fontWeight: 700,
                padding: '5px 11px',
                borderRadius: 999,
                cursor: 'pointer',
                border: active ? '1px solid var(--color-primary)' : '1px solid var(--color-line)',
                background: active ? 'var(--color-primary)' : 'transparent',
                color: active ? '#fff' : 'var(--color-ink)',
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function NeighborhoodsPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [priceFilter, setPriceFilter] = useState<PriceFilter>('any');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const filtersActive = typeFilter !== 'all' || priceFilter !== 'any' || statusFilter !== 'all';
  const shownCount = NEIGHBORHOODS.filter((n) => matchesFilters(n, typeFilter, priceFilter, statusFilter)).length;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Neighborhoods</h1>
        <p className="page-subtitle">
          All {NEIGHBORHOODS.length} neighborhoods in Babcock Ranch, grouped by where they are in town. Prices and
          homes for sale are as of {NEIGHBORHOODS_AS_OF} — tap a neighborhood for its photo and details.
        </p>
      </div>

      <div className="card" style={{ padding: '6px 16px 16px' }}>
        <FilterRow
          label="Type"
          value={typeFilter}
          onChange={(v) => {
            setTypeFilter(v);
            setOpenId(null);
          }}
          options={[
            { value: 'all', label: 'All' },
            { value: 'gated', label: 'Gated' },
            { value: '55+', label: '55+' },
            { value: 'golf', label: 'Golf' },
            { value: 'rentals', label: 'Rentals' },
          ]}
        />
        <FilterRow
          label="Price"
          value={priceFilter}
          onChange={(v) => {
            setPriceFilter(v);
            setOpenId(null);
          }}
          options={[
            { value: 'any', label: 'Any price' },
            { value: 'u350', label: 'Under $350K' },
            { value: 'm3560', label: '$350–600K' },
            { value: 'o600', label: '$600K+' },
          ]}
        />
        <FilterRow
          label="Status"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setOpenId(null);
          }}
          options={[
            { value: 'all', label: 'All' },
            { value: 'building', label: 'Still building' },
            { value: 'established', label: 'Built out / resale' },
            { value: 'notyet', label: 'Coming soon' },
          ]}
        />
        {filtersActive && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12,
              fontSize: 13,
              color: 'var(--color-ink-muted)',
            }}
          >
            <span>
              Showing <strong style={{ color: 'var(--color-ink)' }}>{shownCount}</strong> of {NEIGHBORHOODS.length}{' '}
              neighborhoods
            </span>
            <button
              type="button"
              onClick={() => {
                setTypeFilter('all');
                setPriceFilter('any');
                setStatusFilter('all');
                setOpenId(null);
              }}
              style={{
                fontSize: 13,
                fontWeight: 700,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-primary)',
                padding: 0,
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {DISTRICTS.map((d) => {
        const items = NEIGHBORHOODS.filter(
          (n) => n.district === d.id && matchesFilters(n, typeFilter, priceFilter, statusFilter),
        );
        if (items.length === 0) return null;
        const totalForSale = items.reduce((sum, n) => sum + (n.forSaleCount ?? 0), 0);
        const minPrice = Math.min(...items.map((n) => n.priceMinK ?? Number.POSITIVE_INFINITY));
        const statLine = `${items.length} neighborhoods · ${totalForSale} homes for sale${
          Number.isFinite(minPrice) ? ` · from $${minPrice}K` : ''
        }`;
        return (
          <div key={d.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ position: 'relative', height: 132, background: d.color }}>
              <img
                src={d.photoUrl}
                alt={d.photoAlt}
                loading="lazy"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0.58))',
                }}
              />
              <div style={{ position: 'absolute', left: 16, right: 16, bottom: 12, color: '#fff' }}>
                <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>{d.title}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.92)', marginTop: 2 }}>{d.blurb}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.95)', marginTop: 4 }}>
                  {statLine}
                </div>
              </div>
            </div>
            {items.map((n, i) => (
              <NeighborhoodRow
                key={n.id}
                n={n}
                district={d}
                last={i === items.length - 1}
                open={openId === n.id}
                onToggle={() => setOpenId(openId === n.id ? null : n.id)}
              />
            ))}
          </div>
        );
      })}

      <p style={{ color: 'var(--color-ink-muted)', fontSize: 13 }}>
        {COMMUNITY_AMENITIES_NOTE} Listing counts are a snapshot, not live MLS data, and change daily. Photos are from
        each neighborhood&rsquo;s own public sales page, linked from its entry.
      </p>
    </div>
  );
}

